import React from "react";
import {
  Workflow, Timer, Hourglass, Wallet, PiggyBank, Bot, ListChecks, Sparkles, Loader2,
  RefreshCw, Flame, Clock, Server, MousePointerClick, Info, X, Download,
} from "lucide-react";
import * as api from "../features/bpm/bpmApi";
import BpmDiagram, { HEAT_COLORS } from "../features/bpm/BpmDiagram";
import Narrative from "../features/bpm/Narrative";
import {
  card, th, td, btn, Kpi, Pill, SEV, Provenance, useAsync, Loading, Err, n, money,
} from "./cyberShared";

/**
 * Business Process Improvement.
 *
 * Reads a BPMN process, attaches per-step operating metrics and the systems/APIs
 * each step touches, and shows where the process loses money and time: rework,
 * manual hand-offs, serial approvals, re-keying, bottlenecks, overlapping tools
 * and unreliable integrations — then asks Bedrock (or a template) for a plan.
 *
 * ASSUMPTION: "analyzing the APIs" means mapping every step to the systems it
 * touches with a MOCK latency, error rate and integration type (API, manual
 * re-key, file transfer, email). Every metric on this page is deterministic demo
 * data; the process structure and the detection arithmetic are real.
 */

const PROVENANCE = {
  note: "Process structure and detection rules are real; every time, volume, rate, latency and "
      + "error figure is mock demonstration data.",
  real: [
    "BPMN structure: lanes, steps, gateways, flows and loops",
    "Detection rules and cost / savings arithmetic",
    "Demand discovery BPMN (when a demand is selected)",
  ],
  generated: [
    "Step touch time, wait time and labour rates",
    "Annual volumes and branch probabilities",
    "System/API latency, error rate and integration type",
    "Licence shares and cost-of-delay rate",
  ],
};

const INTEGRATION_TONE = {
  api: { bg: "#0f323c", fg: "#85e3af", label: "API" },
  manual: { bg: "#322c35", fg: "#ffa685", label: "Manual re-key" },
  file: { bg: "#313633", fg: "#facb7e", label: "File transfer" },
  email: { bg: "#1a2a49", fg: "#c4b3ff", label: "Email" },
};

const CATEGORY_ICON = {
  "Rework loop": RefreshCw,
  "Manual handoff": MousePointerClick,
  "Serial approvals": ListChecks,
  "Swivel-chair re-keying": MousePointerClick,
  "Automation candidate": Bot,
  Bottleneck: Hourglass,
  "Redundant systems": Server,
  "Slow / error-prone API": Flame,
};

const compact = (v) => {
  const a = Math.abs(v ?? 0);
  if (a >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `$${(v / 1e3).toFixed(0)}k`;
  return money(v);
};
const hrs = (h) => (h >= 48 ? `${(h / 24).toFixed(1)} d` : `${(+h).toFixed(1)} h`);
const latency = (ms) => {
  if (!ms) return "—";
  if (ms >= 3600000) return `${(ms / 3600000).toFixed(0)} h batch`;
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`;
  return `${ms} ms`;
};

const MockBadge = () => (
  <span title="Mock demonstration figure"
        style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, letterSpacing: .4,
                 padding: "1px 5px", borderRadius: 4, background: "#313633",
                 color: "#facb7e", textTransform: "uppercase" }}>mock</span>
);

const sevTone = (s) => SEV[s] || SEV.NONE;

function IntegrationPill({ type }) {
  const t = INTEGRATION_TONE[type] || INTEGRATION_TONE.manual;
  return <Pill tone={t}>{t.label}</Pill>;
}

/* ------------------------------------------------------------------ step panel */
function StepPanel({ step, findings, onClose, onFinding }) {
  if (!step) {
    return (
      <div style={{ ...card, padding: 16, fontSize: 12.5, color: "#8aa0b6", lineHeight: 1.6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, color: "#c3d2e0",
                      fontWeight: 700, marginBottom: 6 }}>
          <MousePointerClick size={14} style={{ color: "#9d80ff" }} /> Step details
        </div>
        Click a task on the diagram to see its metrics and the systems and APIs it touches.
        Badges show how many findings affect a step; colour shows the heatmap.
      </div>
    );
  }
  const mine = findings.filter((f) => step.findingIds?.includes(f.id));
  const row = (label, value, mock = true) => (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12,
                  padding: "4px 0", borderBottom: "1px solid #102942" }}>
      <span style={{ color: "#8aa0b6" }}>{label}</span>
      <span style={{ color: "#e8eef5", fontVariantNumeric: "tabular-nums" }}>
        {value}{mock && <MockBadge />}
      </span>
    </div>
  );
  return (
    <div style={{ ...card, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#e8eef5" }}>{step.name}</div>
          <div style={{ fontSize: 11, color: "#6b8aa8", marginTop: 2 }}>
            {step.lane} · {step.type.replace(/([A-Z])/g, " $1").toLowerCase()}
          </div>
        </div>
        <button onClick={onClose} title="Close"
                style={{ background: "none", border: "none", color: "#8aa0b6", cursor: "pointer" }}>
          <X size={14} />
        </button>
      </div>
      {step.isTask ? (
        <>
          {row("Runs per instance", step.visitsPerInstance.toFixed(2), false)}
          {row("Volume / yr", n(step.volumePerYear))}
          {row("Touch time", `${step.touchMin} min`)}
          {row("Wait before / in step", hrs(step.waitHours))}
          {row("Labour rate", `$${step.laborRate}/h`)}
          {row("Annual cost", money(step.annualCost))}
          {step.annualExceptionCost > 0 && row("of which exception handling", money(step.annualExceptionCost))}
          {row("Delay / yr", `${n(step.annualDelayHours)} instance-h`)}
          {row("Hand-off in", step.handoff ? "Yes — from another lane" : "No", false)}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "8px 0 4px" }}>
            {step.approval && <Pill tone={{ bg: "#1a2a49", fg: "#c4b3ff" }}>Approval</Pill>}
            {step.ruleBased && <Pill tone={{ bg: "#0f323c", fg: "#85e3af" }}>Rule-based</Pill>}
            {step.inRework && <Pill tone={SEV.HIGH}>In rework loop</Pill>}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12, color: "#8aa0b6" }}>
          {step.type.includes("Gateway") ? "Decision point — routes work; no touch time."
                                         : "Event — no work is done here."}
        </div>
      )}

      {step.systems?.length > 0 && (
        <>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6b8aa8", textTransform: "uppercase",
                        letterSpacing: .4, margin: "12px 0 6px" }}>Systems & APIs touched</div>
          {step.systems.map((s, i) => (
            <div key={i} style={{ padding: "7px 0", borderBottom: "1px solid #102942" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#e8eef5" }}>{s.system}</span>
                <IntegrationPill type={s.integration} />
              </div>
              <div style={{ fontSize: 11, color: "#8aa0b6", marginTop: 2 }}>
                {s.api}
                {(s.integration === "api" || s.integration === "file") &&
                  ` · ${latency(s.latencyMs)} · ${s.errorRate}% errors`}
                {s.keys?.length > 0 && ` · keys: ${s.keys.join(", ")}`}
              </div>
            </div>
          ))}
        </>
      )}

      {mine.length > 0 && (
        <>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6b8aa8", textTransform: "uppercase",
                        letterSpacing: .4, margin: "12px 0 6px" }}>Findings on this step</div>
          {mine.map((f) => (
            <button key={f.id} onClick={() => onFinding(f.id)}
                    style={{ display: "flex", gap: 7, alignItems: "center", width: "100%",
                             textAlign: "left", background: "none", border: "none",
                             padding: "4px 0", cursor: "pointer", color: "#c3d2e0", fontSize: 12 }}>
              <Pill tone={sevTone(f.severity)}>{f.id}</Pill>
              <span style={{ flex: 1 }}>{f.title}</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- findings */
function Findings({ findings, steps, active, onToggle }) {
  const name = Object.fromEntries(steps.map((s) => [s.id, s.name]));
  return (
    <div style={{ ...card, padding: 16 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 3px", color: "#e8eef5" }}>
        Findings, by estimated savings
      </h3>
      <p style={{ fontSize: 11.5, color: "#6b8aa8", margin: "0 0 10px" }}>
        Click a finding to highlight the affected steps on the diagram. Savings are gross and
        overlap between findings <MockBadge />
      </p>
      {findings.length === 0 && (
        <div style={{ fontSize: 12.5, color: "#85e3af" }}>No issues detected by the rules.</div>
      )}
      {findings.map((f) => {
        const Icon = CATEGORY_ICON[f.category] || Info;
        const on = active === f.id;
        return (
          <div key={f.id} onClick={() => onToggle(f.id)}
               style={{ padding: "10px 12px", marginBottom: 8, borderRadius: 10, cursor: "pointer",
                        border: `1px solid ${on ? "#9d80ff" : "#1c3d5c"}`,
                        background: on ? "#12284a" : "#0b1a2d" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Pill tone={sevTone(f.severity)}>{f.severity}</Pill>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11,
                             color: "#8aa0b6" }}>
                <Icon size={12} /> {f.category}
              </span>
              <span style={{ fontSize: 10.5, color: "#6b8aa8" }}>{f.id}</span>
              <span style={{ marginLeft: "auto", fontSize: 14, fontWeight: 700, color: "#85e3af",
                             fontVariantNumeric: "tabular-nums" }}>
                {compact(f.savingsPerYear)}<span style={{ fontSize: 11, color: "#8aa0b6" }}>/yr</span>
              </span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#e8eef5", margin: "6px 0 3px" }}>
              {f.title}
            </div>
            <div style={{ fontSize: 12, color: "#a8bccf", lineHeight: 1.5 }}>{f.detail}</div>
            {on && (
              <div style={{ fontSize: 12, color: "#c3d2e0", marginTop: 6, lineHeight: 1.5 }}>
                <strong style={{ color: "#c4b3ff" }}>Recommendation: </strong>{f.recommendation}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6, fontSize: 11,
                          color: "#8aa0b6" }}>
              <span>Labour {compact(f.laborSavingsPerYear)}</span>
              <span>Delay {compact(f.delaySavingsPerYear)}</span>
              {f.hoursSavedPerInstance > 0 && <span>−{hrs(f.hoursSavedPerInstance)} per instance</span>}
            </div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
              {f.affectedSteps.map((s) => (
                <span key={s} style={{ fontSize: 10.5, padding: "1px 7px", borderRadius: 999,
                                       background: "#102942", color: "#a8bccf" }}>
                  {name[s] || s}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------- API table */
function ApiTable({ apis, note }) {
  return (
    <div style={{ ...card, padding: 16 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 3px", color: "#e8eef5",
                   display: "flex", alignItems: "center", gap: 7 }}>
        <Server size={14} style={{ color: "#9d80ff" }} /> Systems & APIs touched
      </h3>
      <p style={{ fontSize: 11.5, color: "#6b8aa8", margin: "0 0 10px", maxWidth: 820 }}>
        <Info size={11} style={{ verticalAlign: -1 }} /> {note}
      </p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>System</th>
            <th style={th}>Interface</th>
            <th style={th}>Integration</th>
            <th style={{ ...th, textAlign: "right" }}>Calls / yr</th>
            <th style={{ ...th, textAlign: "right" }}>Latency</th>
            <th style={{ ...th, textAlign: "right" }}>Error rate</th>
            <th style={th}>Issue</th>
          </tr></thead>
          <tbody>
            {apis.map((a, i) => (
              <tr key={i}>
                <td style={td}>
                  <div style={{ fontWeight: 600, color: "#e8eef5" }}>{a.system}</div>
                  <div style={{ fontSize: 10.5, color: "#6b8aa8" }}>{a.category}</div>
                </td>
                <td style={{ ...td, fontSize: 11.5 }}>{a.api}</td>
                <td style={td}><IntegrationPill type={a.integration} /></td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {n(a.callsPerYear)}
                </td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums",
                             color: a.latencyMs > 2000 ? "#facb7e" : "#c3d2e0" }}>
                  {a.integration === "api" || a.integration === "file" ? latency(a.latencyMs) : "—"}
                </td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums",
                             color: a.errorRate > 2 ? "#ffa685" : "#c3d2e0" }}>
                  {a.integration === "api" || a.integration === "file" ? `${a.errorRate}%` : "—"}
                </td>
                <td style={{ ...td, fontSize: 11.5, color: a.issue ? "#facb7e" : "#6b8aa8" }}>
                  {a.issue || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- plan card */
function PlanCard({ processId }) {
  const [state, setState] = React.useState({ loading: false, data: null, error: "" });
  React.useEffect(() => { setState({ loading: false, data: null, error: "" }); }, [processId]);

  const run = async (refresh = false) => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const data = await api.getNarrative(processId, { refresh });
      setState({ loading: false, data, error: "" });
    } catch (e) {
      setState({ loading: false, data: null, error: e.message || String(e) });
    }
  };
  const d = state.data;
  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0, color: "#e8eef5",
                     display: "flex", alignItems: "center", gap: 7 }}>
          <Sparkles size={14} style={{ color: "#9d80ff" }} /> Improvement plan
        </h3>
        {d && (
          <span style={{ fontSize: 10.5, color: d.source === "bedrock" ? "#85e3af" : "#facb7e" }}>
            {d.source === "bedrock"
              ? `AI-generated${d.cached ? " (cached)" : ""}${d.model ? ` · ${d.model}` : ""}`
              : "Template — AI unavailable, built from the findings"}
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {d && (
            <button style={btn} disabled={state.loading} onClick={() => run(true)}>
              <RefreshCw size={13} /> Regenerate
            </button>
          )}
          <button style={{ ...btn, background: "#9d80ff", borderColor: "#9d80ff", color: "#0b2035" }}
                  disabled={state.loading} onClick={() => run(false)}>
            {state.loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {state.loading ? "Generating…" : "Generate improvement plan"}
          </button>
        </div>
      </div>
      {!d && !state.loading && !state.error && (
        <p style={{ fontSize: 12, color: "#6b8aa8", margin: "10px 0 0" }}>
          Writes an executive summary, prioritised recommendations and a described to-be process
          from the findings above. Uses Bedrock when available; otherwise a deterministic template.
        </p>
      )}
      {state.loading && (
        <p style={{ fontSize: 12, color: "#8aa0b6", margin: "10px 0 0" }}>
          Drafting the plan — this can take 10–30 seconds the first time.
        </p>
      )}
      {state.error && <div style={{ marginTop: 10 }}><Err>{state.error}</Err></div>}
      {d && <div style={{ marginTop: 8 }}><Narrative text={d.narrative} /></div>}
    </div>
  );
}

/* --------------------------------------------------------------------- page */
const DEFAULT_PROCESS = "it-service-desk";
const queryProcess = () => {
  try {
    return new URLSearchParams(window.location.search).get("process") || null;
  } catch {
    return null;
  }
};

/**
 * `processId` (from the dashboard shell, e.g. after an import) or `?process=<id>` in
 * the URL preselects a process; otherwise the first sample is shown.
 */
export default function BpmImprovementPage({ processId = null } = {}) {
  const list = useAsync(() => api.listProcesses(), [processId]);
  const [pid, setPid] = React.useState(() => processId || queryProcess() || DEFAULT_PROCESS);
  React.useEffect(() => { if (processId) setPid(processId); }, [processId]);
  const [heatMode, setHeatMode] = React.useState("cost");
  const [activeFinding, setActiveFinding] = React.useState(null);
  const [selected, setSelected] = React.useState(null);

  const detail = useAsync(
    () => Promise.all([api.getProcess(pid), api.analyzeProcess(pid)])
      .then(([process, analysis]) => ({ process, analysis })),
    [pid]);

  React.useEffect(() => { setActiveFinding(null); setSelected(null); }, [pid]);

  const analysis = detail.data?.analysis;
  const process = detail.data?.process;
  const findings = React.useMemo(() => analysis?.findings || [], [analysis]);
  const steps = React.useMemo(() => analysis?.steps || [], [analysis]);
  const highlight = React.useMemo(
    () => findings.find((f) => f.id === activeFinding)?.affectedSteps || [],
    [findings, activeFinding]);
  const selectedStep = steps.find((s) => s.id === selected) || null;

  const processes = list.data?.processes || [];
  const samples = processes.filter((p) => p.source === "sample");
  const imported = processes.filter((p) => p.source === "import");
  const demands = processes.filter((p) => p.source === "demand");
  const current = processes.find((p) => p.id === pid);

  const toggleFinding = (id) => setActiveFinding((a) => (a === id ? null : id));

  return (
    <div style={{ color: "#e8eef5" }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#e8eef5", margin: 0,
                     display: "flex", alignItems: "center", gap: 9 }}>
          <Workflow size={20} style={{ color: "#9d80ff" }} /> Process Analysis
        </h1>
        <p style={{ fontSize: 13, color: "#8aa0b6", margin: "6px 0 0", maxWidth: 820 }}>
          Analyses a BPMN workflow and the systems each step touches to show whether the process
          is optimal or is costing money and time through rework, hand-offs, serial approvals,
          re-keying and unreliable integrations — and what to change.
        </p>
      </div>

      <Provenance provenance={PROVENANCE} compact />

      {/* ---- picker */}
      <div style={{ ...card, padding: "12px 14px", marginBottom: 14, display: "flex", gap: 12,
                    alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#6b8aa8", textTransform: "uppercase",
                       letterSpacing: .4 }}>Process</span>
        <select value={pid} onChange={(e) => setPid(e.target.value)}
                style={{ background: "#081a2c", color: "#e8eef5", border: "1px solid #2c567b",
                         borderRadius: 8, padding: "7px 10px", fontSize: 13, minWidth: 300 }}>
          {list.loading && <option value={pid}>Loading…</option>}
          {!list.loading && !current && <option value={pid}>{pid}</option>}
          <optgroup label="Samples">
            {samples.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </optgroup>
          {imported.length > 0 && (
            <optgroup label="Imported">
              {imported.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </optgroup>
          )}
          {demands.length > 0 && (
            <optgroup label="Demands (discovery BPMN)">
              {demands.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </optgroup>
          )}
        </select>
        <span style={{ fontSize: 12, color: "#8aa0b6", flex: 1, minWidth: 220 }}>
          {current?.description}
          {current?.owner && <span style={{ color: "#6b8aa8" }}> · Owner: {current.owner}</span>}
        </span>
        {list.error && <span style={{ fontSize: 11.5, color: "#ffa685" }}>{list.error}</span>}
        <a href={api.exportUrl(pid)} download title="Download this process as BPMN 2.0"
           style={{ ...btn, textDecoration: "none" }}>
          <Download size={13} /> Download BPMN
        </a>
      </div>

      {detail.loading && <Loading />}
      {detail.error && <Err>{detail.error}</Err>}

      {analysis && process && (
        <>
          {/* ---- KPIs */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <Kpi icon={Timer} label={<>Cycle time<MockBadge /></>}
                 value={`${analysis.kpis.cycleTimeDays} d`}
                 sub={`${analysis.kpis.cycleTimeHours} h per instance · ${n(analysis.kpis.annualVolume)}/yr`} />
            <Kpi icon={Clock} label={<>Touch vs wait<MockBadge /></>}
                 value={`${analysis.kpis.touchPct}% / ${analysis.kpis.waitPct}%`}
                 tone={analysis.kpis.waitPct > 80 ? "#f7a828" : "#e8eef5"}
                 sub={`${analysis.kpis.touchHours} h work, ${hrs(analysis.kpis.waitHours)} waiting`} />
            <Kpi icon={Wallet} label={<>Annual process cost<MockBadge /></>}
                 value={compact(analysis.kpis.annualProcessCost)}
                 sub={`${money(analysis.kpis.costPerInstance)}/instance · +${compact(analysis.kpis.annualDelayCost)} delay`} />
            <Kpi icon={PiggyBank} label={<>Savings / yr<MockBadge /></>}
                 value={compact(analysis.kpis.netSavingsEstimate)} tone="#34d17a"
                 sub={`net of overlap · ${compact(analysis.kpis.identifiedSavingsPerYear)} gross`} />
            <Kpi icon={Bot} label={<>Automation potential<MockBadge /></>}
                 value={`${analysis.kpis.automationPotentialPct}%`} tone="#c4b3ff"
                 sub="of touch time" />
            <Kpi icon={ListChecks} label="Findings" value={analysis.kpis.findingsCount}
                 tone={(analysis.kpis.findingsBySeverity.CRITICAL || 0) ? "#ff6a33" : "#e8eef5"}
                 sub={["CRITICAL", "HIGH", "MEDIUM", "LOW"]
                   .filter((s) => analysis.kpis.findingsBySeverity[s])
                   .map((s) => `${analysis.kpis.findingsBySeverity[s]} ${s.toLowerCase()}`).join(" · ")} />
          </div>

          {/* ---- diagram + side panel */}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
                        marginBottom: 14 }}>
            <div style={{ ...card, padding: 12, flex: "3 1 620px", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10,
                            flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>Process heatmap</span>
                <div style={{ display: "flex", border: "1px solid #2c567b", borderRadius: 8,
                              overflow: "hidden" }}>
                  {[["cost", "Cost"], ["delay", "Delay"]].map(([k, label]) => (
                    <button key={k} onClick={() => setHeatMode(k)}
                            style={{ padding: "4px 11px", fontSize: 11.5, fontWeight: 600,
                                     border: "none", cursor: "pointer",
                                     background: heatMode === k ? "#9d80ff" : "transparent",
                                     color: heatMode === k ? "#0b2035" : "#c3d2e0" }}>
                      {label}
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10.5,
                              color: "#8aa0b6" }}>
                  low
                  {HEAT_COLORS.map((c) => (
                    <span key={c} style={{ width: 16, height: 9, borderRadius: 2, background: c,
                                           display: "inline-block" }} />
                  ))}
                  high {heatMode === "cost" ? "annual cost" : "delay hours"}
                </div>
                {activeFinding && (
                  <button style={{ ...btn, padding: "3px 9px", fontSize: 11, marginLeft: "auto" }}
                          onClick={() => setActiveFinding(null)}>
                    <X size={12} /> Clear highlight ({activeFinding})
                  </button>
                )}
              </div>
              <BpmDiagram xml={process.xml} steps={steps} findings={findings}
                          heatMode={heatMode} highlight={highlight} selectedId={selected}
                          onSelect={setSelected} height={470} />
            </div>
            <div style={{ flex: "1 1 300px", maxWidth: 420, minWidth: 280 }}>
              <StepPanel step={selectedStep} findings={findings}
                         onClose={() => setSelected(null)} onFinding={toggleFinding} />
            </div>
          </div>

          {/* ---- findings + plan */}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
                        marginBottom: 14 }}>
            <div style={{ flex: "1 1 480px", minWidth: 0 }}>
              <Findings findings={findings} steps={steps} active={activeFinding}
                        onToggle={toggleFinding} />
            </div>
            <div style={{ flex: "1 1 420px", minWidth: 0 }}>
              <PlanCard processId={pid} />
            </div>
          </div>

          <ApiTable apis={analysis.apis} note={analysis.assumptions?.note} />
        </>
      )}
    </div>
  );
}
